import { Component, EventEmitter, Input, OnInit, Output, Pipe, PipeTransform } from "@angular/core";
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Tools } from "@comunicare/comunicare-helper";
import {
  FHIRHelper,
  IAddress,
  IDevicesAvailableToOrder,
  INewPrescription,
  IPrescriptionInCareplan,
  OnlineDevice,
  OrganisationType,
  PRESCRIPTION_TYPE,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { first } from "rxjs/operators";
import { OrganizationsService } from "src/app/providers/organizations.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { PrescriptionService } from "src/app/providers/prescription.service";
import { SessionService } from "src/app/providers/session.service";
@Pipe({
  name: "getDevicePrice",
  pure: false,
})
export class GetDevicePricePipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}
  transform(form: UntypedFormGroup, devices: IDevicesAvailableToOrder[]): string {
    const valueForm = form.getRawValue();
    const deviceSelected = devices.find((d) => d.name === valueForm.selectedDevice);
    if (!deviceSelected) {
      return "";
    }
    let price = deviceSelected.price;
    let detail = this.translateService.instant("forms.prescriptionDevice.originalPrice") + price + "€\n";
    if (deviceSelected.priceByDay && valueForm.estimatedDays) {
      price += deviceSelected.priceByDay * valueForm.estimatedDays;
      detail +=
        this.translateService.instant("forms.prescriptionDevice.priceByDay") +
        deviceSelected.priceByDay +
        "€ * " +
        valueForm.estimatedDays +
        "\n";
    }
    if (deviceSelected.additionalPrice && valueForm.nbPatch) {
      price += deviceSelected.additionalPrice * valueForm.nbPatch;
      detail +=
        this.translateService.instant("forms.prescriptionDevice.patch") +
        deviceSelected.additionalPrice +
        "€ * " +
        valueForm.nbPatch +
        "\n";
    }
    return detail + this.translateService.instant("forms.prescriptionDevice.total") + price.toString() + "€";
  }
}
@Component({
  selector: "app-prescription-devices-form",
  templateUrl: "./prescription-devices-form.component.html",
  styleUrls: ["./prescription-devices-form.component.scss"],
})
export class PrescriptionDevicesFormComponent implements OnInit {
  public prescriptionDeviceForm: UntypedFormGroup;
  public isLoading = true;
  public devicesToOrder: IDevicesAvailableToOrder[];
  public patientAddress: IAddress;
  public orgAddress: IAddress;
  public hasPatch = false;
  @Output() finish = new EventEmitter<void>();
  @Input() careplanPrescription: IPrescriptionInCareplan;

  constructor(
    private fb: FormBuilder,
    private prescriptionService: PrescriptionService,
    private patientRegistrationService: PatientRegistrationService,
    private sessionService: SessionService,
    private organizationService: OrganizationsService
  ) {}
  ngOnInit(): void {
    this.patientAddress = this.patientRegistrationService.patientUser.patient.address?.[0] ?? undefined;
    this.organizationService
      .list(this.sessionService.organization.reference, this.sessionService.organization.type as OrganisationType)
      .pipe(first())
      .subscribe((result) => {
        if (result?.length) {
          this.orgAddress = result[0].address?.[0] ?? undefined;
        }
      });
    this.prescriptionService
      .getDevicesToOrder(
        this.careplanPrescription.prescriptionType === PRESCRIPTION_TYPE.BYTEFLIES_DEVICE ? OnlineDevice.BYTEFLIES : OnlineDevice.DEXCOM
      )
      .pipe(first())
      .subscribe((devices) => {
        this.devicesToOrder = devices;
        this.prescriptionDeviceForm = this.fb.group({
          name: new FormControl<string>(undefined, Validators.required),
          selectedDevice: new FormControl<string>(this.devicesToOrder[0].name),
          estimatedDays: new FormControl<number>(undefined),
          city: new FormControl<string>(undefined, Validators.required),
          country: new FormControl<string>(undefined, Validators.required),
          streetName: new FormControl<string>(undefined, Validators.required),
          streetNumber: new FormControl<string>(undefined, Validators.required),
          postalCode: new FormControl<string>(undefined, Validators.required),
          boxNumber: new FormControl<string>(undefined),
          nbPatch: new FormControl<number>(undefined),
        });
        this.prescriptionDeviceForm.get("name").setValue(this.generateOrderName());
        this.prescriptionDeviceForm.get("name").disable();
        this.isLoading = false;
      });
  }

  public apply(): void {
    const formValues = this.prescriptionDeviceForm.getRawValue();
    if (
      (!Tools.isDefined(formValues.name) || formValues.name === "") &&
      !Tools.isDefined(formValues.selectedDevice) &&
      !Tools.isDefined(formValues.estimatedDays) &&
      (!Tools.isDefined(formValues.city) || formValues.city === "") &&
      (!Tools.isDefined(formValues.country) || formValues.country === "") &&
      (!Tools.isDefined(formValues.streetNumber) || formValues.streetNumber === "") &&
      (!Tools.isDefined(formValues.streetName) || formValues.streetName === "") &&
      (!Tools.isDefined(formValues.postalCode) || formValues.postalCode === "") &&
      (!Tools.isDefined(formValues.boxNumber) || formValues.boxNumber === "") &&
      !Tools.isDefined(formValues.nbPatch)
    ) {
      // no change made, skip the form
      this.finish.emit();
    } else if (this.prescriptionDeviceForm.valid) {
      this.isLoading = true;
      const deviceSelected = this.devicesToOrder.find((d) => d.name === formValues.selectedDevice);
      this.prescriptionService
        .orderDevice(
          FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value,
          formValues.name,
          formValues.estimatedDays,
          formValues.city,
          formValues.country,
          formValues.streetName,
          formValues.streetNumber,
          formValues.postalCode,
          deviceSelected.name,
          deviceSelected.devicesTypes,
          deviceSelected.devicesBrand,
          formValues.boxNumber,
          formValues.nbPatch,
          deviceSelected.externalRessourceRef
        )
        .subscribe((deviceOrder) => {
          if (Tools.isDefined(deviceOrder)) {
            const newPrescription: INewPrescription = {
              patientId: FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value,
              responsibleId: this.sessionService.account.caremateIdentifier,
              serviceId: this.patientRegistrationService.healthcareService.reference,
              careplanId: FHIRHelper.getMainIdentifier(this.patientRegistrationService.careplan).value,
              officialCareplanId:
                this.careplanPrescription.officialCareplan ?? FHIRHelper.getMainIdentifier(this.patientRegistrationService.careplan).value,
              occurence: {
                occurenceDateTime: deviceOrder.orderDate,
                occurenceTiming: {
                  duration: deviceOrder.estimatedDays ?? 0,
                  durationUnit: "d",
                },
                renewal: false,
              },
              deviceOrdersIds: [deviceOrder.orderId],
              pricing: {
                pricingCareTeam: {
                  coefficient: 1, // no coefficient
                  codeVAT: 0, // TVA already included
                },
                billingFrequency: 1,
                billingFrequencyUnits: "m",
              },
              askApproval: false,
            };
            this.prescriptionService
              .createPrescription(newPrescription, this.careplanPrescription.prescriptionType)
              .pipe(first())
              .subscribe((result) => {
                this.patientRegistrationService.onPrescriptionCreated$.next(result);
                this.finish.emit();
                this.isLoading = false;
              });
          }
        });
    } else {
      this.prescriptionDeviceForm.markAllAsTouched();
    }
  }

  private generateOrderName(): string {
    if (!this.prescriptionDeviceForm.get("selectedDevice").value) {
      return "";
    }
    return (
      this.prescriptionDeviceForm.get("selectedDevice").value +
      "-" +
      this.patientRegistrationService.patientUser.patient.name[0].given[0].substring(0, 1).toUpperCase() +
      this.patientRegistrationService.patientUser.patient.name[0].family[0].substring(0, 1).toUpperCase() +
      "-" +
      moment().valueOf().toString()
    );
  }

  public onDeviceChange(): void {
    this.prescriptionDeviceForm.get("name").setValue(this.generateOrderName());
    const deviceSelected = this.devicesToOrder.find((d) => d.name === this.prescriptionDeviceForm.get("selectedDevice").value);
    this.hasPatch = !!deviceSelected?.additionalPrice;
    this.prescriptionDeviceForm.get("nbPatch").setValue(this.hasPatch ? 1 : undefined);
  }

  public setAddress(address: IAddress): void {
    if (address.line?.length === 1 && address.line?.includes(",")) {
      address.line = address.line[0].split(",");
    }
    address.line?.sort((a, b) => b.length - a.length);
    this.prescriptionDeviceForm.patchValue({
      city: address.city,
      country: address.country,
      streetName: address.line?.[0] ?? undefined,
      streetNumber: address.line?.length > 1 ? address.line[1].trim() : undefined,
      postalCode: address.postalCode,
    });
  }
}
